import { isRecord } from '@apps/web/components/phoebe_chat/record_utils';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat/transcript_types';
import { type UserQuestionItem } from '@apps/web/components/phoebe_chat/user_question_model';

export type UserQuestionActionStyle = 'primary' | 'secondary' | 'ghost';

export type UserQuestionPresentationAction = {
    readonly value: string;
    readonly label: string;
    readonly style: UserQuestionActionStyle;
};

export type UserQuestionPresentation =
    | {
          readonly type: 'actions';
          readonly title: string | null;
          readonly description: string | null;
          readonly actions: Array<UserQuestionPresentationAction>;
      }
    | {
          readonly type: 'list';
          readonly title: string;
          readonly description: string | null;
          readonly items: Array<{
              readonly id: string;
              readonly title: string;
              readonly detail: string | null;
              readonly tone: 'neutral' | 'success' | 'warning' | 'danger';
          }>;
          readonly initialVisibleCount: number;
          readonly selection: 'single' | 'multiple';
      }
    | {
          readonly type: 'message_preview';
          readonly message: string;
          readonly recipientCount: number;
          readonly recipientNoun: string;
          readonly recipients: Array<string>;
          readonly actions: Array<UserQuestionPresentationAction>;
      };

function optionalString(value: unknown): string | null {
    return typeof value === 'string' && value.trim().length > 0
        ? value.trim()
        : null;
}

function normalizePresentationActions(
    rawActions: unknown,
    choices: ReadonlyArray<string>,
): Array<UserQuestionPresentationAction> | null {
    if (!Array.isArray(rawActions) || rawActions.length !== choices.length) {
        return null;
    }
    const actions: Array<UserQuestionPresentationAction> = [];
    const seenValues = new Set<string>();
    let primaryCount = 0;
    for (const [index, rawAction] of rawActions.entries()) {
        if (!isRecord(rawAction)) {
            return null;
        }
        const value = optionalString(rawAction['value']);
        const label = optionalString(rawAction['label']);
        const rawStyle = rawAction['style'];
        if (
            value === null ||
            label === null ||
            value !== choices[index] ||
            seenValues.has(value) ||
            (rawStyle !== 'primary' &&
                rawStyle !== 'secondary' &&
                rawStyle !== 'ghost')
        ) {
            return null;
        }
        if (rawStyle === 'primary') {
            primaryCount += 1;
        }
        seenValues.add(value);
        actions.push({ value, label, style: rawStyle });
    }
    return primaryCount === 1 ? actions : null;
}

export function getUserQuestionPresentation(
    entry: ToolExecutionEntry,
    questions: ReadonlyArray<UserQuestionItem>,
): UserQuestionPresentation | null {
    const question = questions[0];
    if (questions.length !== 1 || question === undefined) {
        return null;
    }
    const args = isRecord(entry.arguments) ? entry.arguments : {};
    const rawPresentation = args['presentation'];
    if (!isRecord(rawPresentation)) {
        return null;
    }
    const type = rawPresentation['type'];
    if (type === 'list') {
        const title = optionalString(rawPresentation['title']);
        const rawItems = rawPresentation['items'];
        if (
            (question.inputType !== 'single_choice' &&
                question.inputType !== 'multi_choice') ||
            question.choices.length < 2 ||
            question.choices.length > 12 ||
            title === null ||
            !Array.isArray(rawItems) ||
            rawItems.length !== question.choices.length
        ) {
            return null;
        }
        const items: Extract<
            UserQuestionPresentation,
            { readonly type: 'list' }
        >['items'] = [];
        for (const [index, rawItem] of rawItems.entries()) {
            if (!isRecord(rawItem)) {
                return null;
            }
            const id = optionalString(rawItem['id']);
            const itemTitle = optionalString(rawItem['title']);
            const rawTone = rawItem['tone'];
            const tone =
                rawTone === undefined
                    ? 'neutral'
                    : rawTone === 'neutral' ||
                        rawTone === 'success' ||
                        rawTone === 'warning' ||
                        rawTone === 'danger'
                      ? rawTone
                      : null;
            if (
                id === null ||
                itemTitle === null ||
                tone === null ||
                id !== question.choices[index]
            ) {
                return null;
            }
            items.push({
                id,
                title: itemTitle,
                detail: optionalString(rawItem['detail']),
                tone,
            });
        }
        const rawInitialVisibleCount = rawPresentation['initial_visible_count'];
        const initialVisibleCount =
            rawInitialVisibleCount === undefined
                ? Math.min(3, items.length)
                : typeof rawInitialVisibleCount === 'number' &&
                    Number.isSafeInteger(rawInitialVisibleCount) &&
                    rawInitialVisibleCount >= 1 &&
                    rawInitialVisibleCount <= items.length
                  ? rawInitialVisibleCount
                  : null;
        if (initialVisibleCount === null) {
            return null;
        }
        return {
            type,
            title,
            description: optionalString(rawPresentation['description']),
            items,
            initialVisibleCount,
            selection:
                question.inputType === 'multi_choice' ? 'multiple' : 'single',
        };
    }

    if (
        question.inputType !== 'single_choice' ||
        question.choices.length < 2 ||
        question.choices.length > 4
    ) {
        return null;
    }
    const actions = normalizePresentationActions(
        rawPresentation['actions'],
        question.choices,
    );
    if (actions === null) {
        return null;
    }

    if (type === 'actions') {
        return {
            type,
            title: optionalString(rawPresentation['title']),
            description: optionalString(rawPresentation['description']),
            actions,
        };
    }

    if (type === 'message_preview') {
        const message = optionalString(rawPresentation['message']);
        const recipientNoun = optionalString(rawPresentation['recipient_noun']);
        const recipientCount = rawPresentation['recipient_count'];
        const rawRecipients = rawPresentation['recipients'];
        if (
            message === null ||
            recipientNoun === null ||
            typeof recipientCount !== 'number' ||
            !Number.isSafeInteger(recipientCount) ||
            recipientCount < 1 ||
            !Array.isArray(rawRecipients)
        ) {
            return null;
        }
        const recipients = rawRecipients
            .map(optionalString)
            .filter((recipient): recipient is string => recipient !== null);
        if (
            recipients.length !== rawRecipients.length ||
            recipients.length === 0 ||
            recipients.length > recipientCount
        ) {
            return null;
        }
        return {
            type,
            message,
            recipientCount,
            recipientNoun,
            recipients: recipients.slice(0, 4),
            actions,
        };
    }

    return null;
}
